import React from 'react';
import PlaybookHeading from '../../../components/playbooks/PlaybookHeading';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const ManagerCommunityStage4 = () => (
  <div className="max-w-none text-gray-100 space-y-10">
    <PlaybookHeading
      title="Stage 4: Sustaining"
      subtitle="Keep momentum and establish a sustainable cadence for your community of practice."
    />

    {/* TL;DR */}
    <ExpandableCallout
      type="info"
      title="What do I need to DO in this stage?"
      defaultOpen={true}
      className="mb-8"
    >
      <p className="mb-2">You need to do 2 things:</p>
      <ol className="list-none pl-0 mb-2 space-y-1">
        <li className="flex items-start">
          <span className="text-primary-light font-bold mr-2">1.</span>
          <span>Support on content and start to make the new meeting cadence the norm.</span>
        </li>
        <li className="flex items-start">
          <span className="text-primary-light font-bold mr-2">2.</span>
          <span>Facilitate any escalations</span>
        </li>
      </ol>
      <p>Here's what we mean...</p>
    </ExpandableCallout>

    <div className="space-y-6">
      <p>
        So you've had a couple of initial calls and agreed your:
      </p>
      <ol className="list-none pl-6 space-y-1">
        <li className="flex items-start">
          <span className="text-primary-light font-bold mr-2">1.</span>
          <span>Ways of working (community charter including meeting cadence)</span>
        </li>
        <li className="flex items-start">
          <span className="text-primary-light font-bold mr-2">2.</span>
          <span>Highest impact initiatives</span>
        </li>
      </ol>
      <p>
        You are perhaps facilitating less and taking more of a backseat.
      </p>
      <p>
        Now what?
      </p>
    </div>

    {/* Sustaining Momentum */}
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white mb-4">Sustaining Momentum</h2>
      <p>Sustaining momentum is the name of the game. There are 2 main things to focus on here:</p>

      {/* 1. Shared Accountability Loop */}
      <h3 className="text-lg font-semibold text-white">1. Create a shared accountability loop.</h3>
      <p>This looks something like:</p>
      <blockquote className="border-l-4 border-primary-light pl-4 text-gray-300 mb-4">
        ⚒️ Workshop → 🌱 Learn together → ⚡ Action → 📢 Publish → 🪞 Feedback
      </blockquote>
      <p>
        For instance, let's say your next PMCoP {'{monthly}'} meet-up that you'd prioritised as being high impact was:
      </p>
      <blockquote className="border-l-4 border-primary-light pl-4 text-gray-300 mb-4">
        "Aligning on the principle accountability of a manager's role at {'{our company}'}"
      </blockquote>
      <p>An accountability loop here might look:</p>
      <ol className="list-none pl-6 space-y-1">
        <li className="flex items-start">
          <span className="text-primary-light font-bold mr-2">1.</span>
          <span>Workshop on the role and agree definition + accountability</span>
        </li>
        <li className="flex items-start">
          <span className="text-primary-light font-bold mr-2">2.</span>
          <span>Learn together what obstacles folks in the PMCoP fulfilling this are (e.g. capacity)</span>
        </li>
        <li className="flex items-start">
          <span className="text-primary-light font-bold mr-2">3.</span>
          <span>Publish and share with broader org</span>
        </li>
        <li className="flex items-start">
          <span className="text-primary-light font-bold mr-2">4.</span>
          <span>Invite Feedback: each PM invites feedback from their respective teams on where they could improve in fulfilment of that accountability
            <ol className="list-decimal pl-8 space-y-1 mt-2 text-gray-300">
              <li>Share personal results and areas to work on within the PMCoP</li>
              <li>Pair up with someone else in the PMCoP with similar or complimentary results</li>
            </ol>
          </span>
        </li>
      </ol>

      {/* 2. Ideal Operating Cadence */}
      <h3 className="text-lg font-semibold text-white">2. Figure out the ideal operating cadence and support on content</h3>
      <p>
        This bit really is very contextual to your org culture and size and maturity of your people manager community so I'm just going to share some examples in the hope that it inspires you or gives you a bit of confidence that what you're seeing is on track.
      </p>
      <blockquote className="border-l-4 border-primary-light pl-4 text-gray-300 mb-4">
        Ask the community.
      </blockquote>
      <p>
        If things are feeling flat, reset and refresh on your team charter. Escalate where needed, particularly if capacity or burnout are prohibitive to progress.
      </p>
    </div>

    {/* John's Blueprint */}
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white mb-4">John's blueprint</h2>
      <h3 className="text-lg font-semibold text-white">The first session to run: Aligning on the role of manager</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          This is obvious but so important so want to call it out here, and it may already have come out of conversations around your team or community charter.
          <div className="mt-4 mb-4">
            <ToolCallout
              tool="miro"
              label="Community Charter Template"
              href="https://miro.com/templates/team-charter/"
              description="A Miro template for running a team or community charter session."
            />
          </div>
        </li>
        <li>Super important to align on expectations of the role:
          <ul className="list-disc pl-6 space-y-1">
            <li>capacity</li>
            <li>purpose</li>
            <li>principle accountability</li>
            <li>the skills we need to be effective</li>
            <li>the conversations we need to be consistent on</li>
            <li>the principles/policies we need to be up to speed on.</li>
          </ul>
        </li>
      </ul>
      <ExpandableCallout type="tip" title="Resource" defaultOpen={true}>
        <p>
          The Lifelabs <a
            href="https://www.lifelabslearning.com/tools/great-managers"
            target="_blank"
            title="Great Manager Playbook"
            
  
            rel="noopener noreferrer"
            className="underline hover:text-primary"
          >great manager playbook</a> is an excellent resource for this
        </p>
      </ExpandableCallout>
      <PlaybookImage
        src="https://auth.openorg.fyi/storage/v1/object/public/resources/people-manager-community/Untitled%202.png"
        alt="Lifelabs Manager definition approach"
        caption="Lifelabs Manager definition approach"
        className="max-w-4xl w-full"
      />
    </div>

    {/* Regular Activities */}
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white">Regular Activities</h3>
      <ul className="list-none pl-6 space-y-1">
        <li className="flex items-start">
          <span className="text-primary-light mr-2">—</span>
          <span>Quarterly Events: Host fixed events to review team health check/pulse survey insights and set priorities on a miro board. Use a shared accountability loop to keep a drumbeat going.</span>
        </li>
        <li className="flex items-start">
          <span className="text-primary-light mr-2">—</span>
          <span>Bi-weekly Updates: Use a shared Kanban board for ongoing updates to maintain accountability.</span>
        </li>
        <li className="flex items-start">
          <span className="text-primary-light mr-2">—</span>
          <span>Monthly Meetups: Organize lean coffee style meetups to discuss topics of interest. Incorporate informal gatherings with pizza and beer to strengthen the community bond.</span>
        </li>
      </ul>
    </div>

    {/* Continuous Engagement */}
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white">Continuous Engagement</h3>
      <ul className="list-none pl-6 space-y-1">
        <li className="flex items-start">
          <span className="text-primary-light mr-2">—</span>
          <span>Roundtables/Clinics: Facilitate ad-hoc sessions based on current issues or requests from community members, with you as People team providing facilitation/expertise and support.</span>
        </li>
      </ul>
    </div>

    {/* Wider Impact */}
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white mb-4">Wider Impact</h2>
      <ExpandableCallout type="tip" title="Wider Impact" defaultOpen={true}>
        <p className="mb-4">
          One of the pitfalls I see communities of practice fall into is that they purely focus on learning and development or introspective manager content as opposed to wider culture interventions that focus on improving the system of work aka the environment for team members to thrive in.
        </p>
        <p className="mb-4">
          Now this depends on your definition of a people manager and the principle accountability of a role a bit, but what's more potent:
        </p>
        <ol className="list-none pl-6 space-y-1 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>A community of practice that focuses on improving their collective craft?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>A community of practice that <strong>also</strong> focuses on improving the environment for their teams through tangible results?</span>
          </li>
        </ol>
        <p className="mb-4">I vote number 2 and would propose a mix of...</p>
        <p className="mb-2">This:</p>
       
        <PlaybookImage
          src="https://auth.openorg.fyi/storage/v1/object/public/resources/people-manager-community/Untitled.png"
          alt="Good community of practice example"
          caption="A community of practice that drives real change."
          className="mt-2 mb-6"
        />
        <p className="mb-2">Not this:</p>
        
        <PlaybookImage
          src="https://auth.openorg.fyi/storage/v1/object/public/resources/people-manager-community/Untitled%201.png"
          alt="Less effective community of practice example"
          caption="A community of practice that is only introspective."
          className="mt-2 mb-4"
        />
      </ExpandableCallout>
    </div>
  </div>
);

export default ManagerCommunityStage4; 